```vue
<template>
  <AuthShell>
    <template #actions>
      <button
        type="button"
        class="btn btn-secondary"
        :disabled="isAuthenticating"
        @click="loginWithMicrosoft"
      >
        <span class="microsoft-icon" aria-hidden="true">
          <span></span><span></span><span></span><span></span>
        </span>

        <span class="microsoft-label">
          {{
            isAuthenticating
              ? 'A iniciar sessão...'
              : microsoftEmail
                ? 'Login Microsoft'
                : 'Entrar com Microsoft'
          }}

          <small v-if="microsoftEmail">
            ({{ microsoftEmail }})
          </small>
        </span>
      </button>

      <button
        v-if="microsoftEmail && !isAuthenticating"
        type="button"
        class="btn btn-link"
        @click="logoutMicrosoftAccount"
      >
        Terminar sessão Microsoft
      </button>
    </template>
  </AuthShell>
</template>

<script>
import Users from '@/api/Users'
import AuthShell from '@/components/auth/AuthShell.vue'
import { loginWithMicrosoftProvider } from '@/services/keycloakAuth'

export default {
  name: 'LoginView',

  components: {
    AuthShell
  },

  data() {
    return {
      microsoftEmail: null,
      isAuthenticating: false
    }
  },

  async mounted() {
    this.cleanKeycloakErrorFromUrl()
    this.loadMicrosoftAccount()

    const rawResponse = sessionStorage.getItem('msal_response')

    if (rawResponse) {
      sessionStorage.removeItem('msal_response')

      await this.processMicrosoftResponse(rawResponse)
      return
    }

    /*
     * Quando o utilizador faz logout explicitamente da aplicação,
     * esta flag permanece ativa.
     *
     * Mesmo que exista uma conta Microsoft guardada no MSAL,
     * não fazemos login automático enquanto a flag existir.
     *
     * A flag só é removida quando o utilizador carrega
     * explicitamente em "Login Microsoft".
     */
    const automaticLoginBlocked =
      sessionStorage.getItem('app_logout_done') === '1'

    if (
      this.microsoftEmail &&
      !automaticLoginBlocked
    ) {
      await this.tryAutomaticMicrosoftLogin()
    }
  },

  methods: {
    cleanKeycloakErrorFromUrl() {
      const hash = window.location.hash || ''

      if (
        hash.includes('error=login_required') ||
        hash.includes('error=access_denied') ||
        hash.includes('error=temporarily_unavailable')
      ) {
        const cleanUrl =
          `${window.location.origin}` +
          `${window.location.pathname}` +
          `${window.location.search}`

        window.history.replaceState(
          {},
          document.title,
          cleanUrl
        )
      }
    },

    loadMicrosoftAccount() {
      const accounts = this.$msal.getAllAccounts()

      if (accounts.length > 0) {
        const account =
          this.$msal.getActiveAccount() ||
          accounts[0]

        this.$msal.setActiveAccount(account)

        this.microsoftEmail =
          account.username ||
          account.idTokenClaims?.preferred_username ||
          account.idTokenClaims?.email ||
          null

        return account
      }

      this.microsoftEmail = null

      return null
    },

    async processMicrosoftResponse(rawResponse) {
      this.isAuthenticating = true

      try {
        const response =
          typeof rawResponse === 'string'
            ? JSON.parse(rawResponse)
            : rawResponse

        const claims =
          response.idTokenClaims || {}

        const email =
          claims.preferred_username ||
          claims.email ||
          response.account?.username

        const oid =
          claims.oid ||
          response.account?.localAccountId

        const name =
          claims.name ||
          response.account?.name ||
          email

        if (!email || !oid) {
          throw new Error(
            'Não foi possível obter o email ou OID da Microsoft.'
          )
        }

        this.microsoftEmail = email

        if (response.account) {
          this.$msal.setActiveAccount(response.account)
        }

        await Users.registerMicrosoftAccount({
          email,
          oid,
          name
        })

        /*
         * O login Microsoft foi iniciado pelo utilizador
         * ou por uma resposta Microsoft válida.
         */
        sessionStorage.removeItem('app_logout_done')

        await loginWithMicrosoftProvider(email)
      } catch (error) {
        console.error(
          'Erro ao registar ou autenticar conta Microsoft:',
          error
        )

        this.isAuthenticating = false
      }
    },

    async tryAutomaticMicrosoftLogin() {
      if (this.isAuthenticating) {
        return
      }

      const automaticLoginBlocked =
        sessionStorage.getItem('app_logout_done') === '1'

      if (automaticLoginBlocked) {
        return
      }

      const account =
        this.$msal.getActiveAccount() ||
        this.$msal.getAllAccounts()[0]

      if (!account) {
        return
      }

      this.isAuthenticating = true

      try {
        this.$msal.setActiveAccount(account)

        const response =
          await this.$msal.acquireTokenSilent({
            account,
            scopes: [
              'openid',
              'profile',
              'email'
            ]
          })

        const claims =
          response.idTokenClaims ||
          account.idTokenClaims ||
          {}

        const email =
          claims.preferred_username ||
          claims.email ||
          response.account?.username ||
          account.username

        const oid =
          claims.oid ||
          response.account?.localAccountId ||
          account.localAccountId

        const name =
          claims.name ||
          response.account?.name ||
          account.name ||
          email

        if (!email) {
          throw new Error(
            'Não foi possível obter o email da conta Microsoft.'
          )
        }

        this.microsoftEmail = email

        if (oid) {
          await Users.registerMicrosoftAccount({
            email,
            oid,
            name
          })
        }

        await loginWithMicrosoftProvider(email)
      } catch (error) {
        console.warn(
          'Não foi possível efetuar login Microsoft automático:',
          error
        )

        this.isAuthenticating = false
      }
    },

    clearMicrosoftLocalState() {
      sessionStorage.removeItem('msal_response')
      sessionStorage.removeItem('microsoft_link_done')
      sessionStorage.removeItem('app_logout_done')

      this.microsoftEmail = null
      this.isAuthenticating = false

      try {
        this.$msal.setActiveAccount(null)
      } catch (error) {
        console.error(
          'Erro ao limpar conta ativa MSAL:',
          error
        )
      }
    },

    async loginWithMicrosoft() {
      if (this.isAuthenticating) {
        return
      }

      /*
       * Foi o utilizador que carregou no botão.
       * A partir daqui já podemos remover o bloqueio
       * criado pelo logout da aplicação.
       */
      sessionStorage.removeItem('app_logout_done')

      const account =
        this.$msal.getActiveAccount() ||
        this.$msal.getAllAccounts()[0]

      if (account) {
        this.isAuthenticating = true

        try {
          this.$msal.setActiveAccount(account)

          const response =
            await this.$msal.acquireTokenSilent({
              account,
              scopes: [
                'openid',
                'profile',
                'email'
              ]
            })

          await this.processMicrosoftResponse({
            account:
              response.account ||
              account,

            idTokenClaims:
              response.idTokenClaims ||
              account.idTokenClaims ||
              {}
          })

          return
        } catch (silentError) {
          console.warn(
            'Sessão Microsoft silenciosa indisponível:',
            silentError
          )

          this.isAuthenticating = false
        }
      }

      try {
        await this.$msal.loginRedirect({
          scopes: [
            'openid',
            'profile',
            'email'
          ],
          prompt: 'select_account'
        })
      } catch (error) {
        console.error(
          'Erro ao iniciar login Microsoft:',
          error
        )

        this.isAuthenticating = false
      }
    },

    async logoutMicrosoftAccount() {
      const account =
        this.$msal.getActiveAccount() ||
        this.$msal.getAllAccounts()[0]

      this.clearMicrosoftLocalState()

      try {
        if (!account) {
          window.location.reload()
          return
        }

        await this.$msal.logoutRedirect({
          account,
          postLogoutRedirectUri:
            `${window.location.origin}` +
            `${window.location.pathname}`
        })
      } catch (error) {
        console.error(
          'Erro ao terminar sessão Microsoft:',
          error
        )

        this.clearMicrosoftLocalState()
        window.location.reload()
      }
    }
  } 
}
</script>

<style scoped>
.microsoft-label {
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.2;
}

.microsoft-label small {
  font-size: 12px;
  opacity: 0.75;
}

.btn-link {
  background: transparent;
  border: none;
  color: #4f63d7;
  cursor: pointer;
  font-size: 14px;
  padding: 8px 12px;
  text-decoration: underline;
}

.btn-link:hover {
  opacity: 0.8;
}

.btn:disabled {
  cursor: wait;
  opacity: 0.7;
}
</style>
```
